// The contract between the CASINO panel and its four game screens
// (casino.md, ECONOMY · CASINO).
//
// `CasinoPanel` owns the host element and the change guard. A game screen
// owns nothing of the DOM: it returns markup as one string, and it acts on
// a released button the panel hands over. Four modules implement this
// contract, one a game — `roulette.ts`, `blackjack.ts`, `slots.ts` and
// `poker.ts` — and the panel picks one on `CasinoTable.game`.
//
// A screen may hold its own small local state (a roulette bet under
// construction, a hold'em bet size), because that state is the reader's
// intent before the wire sees it. Everything the server rules on comes
// from the frames in `GameCtx.slice` and nowhere else.

import type { MapKeyId } from '../../gen/presentation';
import type { CasinoRules } from '../../gen/proto/aquaphobia';
import type { CasinoIntent } from '../../net/connection';
import type { CasinoSlice } from './casinostate';

/** What a game screen reads and the two things it may do. */
export interface GameCtx {
  /** The last three casino frames and the table's arrival stamp. */
  slice: CasinoSlice;
  /**
   * The hello catalog's casino rule table (R355). Every figure a sentence
   * states comes from here. Null before the hello arrived.
   */
  rules: CasinoRules | null;
  /** The reader's credits, for an affordability gate on a control. */
  credits: number;
  /** A `performance.now()` reading of this render pass. */
  nowMs: number;
  /**
   * The stake tier this reader picked, a `CasinoStake.tier`, or null while
   * the reader picked none (R2143). ONE tier serves the lobby and the
   * machine (R2165): `pickTier` of `lobby.ts` turns it into the tier in
   * force.
   */
  tier: number | null;
  /** The world time of day in game-seconds (`state.ts worldClockSec`): the
   *  club head states the next turn of the light from it (R2165). */
  clockSec: number;
  /** True under reduced motion: the wheel and the reels draw the result at once. */
  still: boolean;
  /**
   * The `performance.now()` of the first render pass that drew the last
   * result, which is the moment its picture started to move. A screen
   * shows the verdict once `landingMs` of `resultcue.ts` has passed since
   * it (R2115).
   */
  resultAtMs: number;
  /**
   * True while the casino draws in the casino pane of a desktop, wide over
   * the map (R2198): the roulette screen then keeps its wheel stage beside
   * the cloth. Absent is the dock column or the sheet.
   */
  wide?: boolean;
  /** Sends one intent on the wire. */
  send(intent: CasinoIntent): void;
  /** The two-press gate (R837): true when the press was the second one. */
  press(key: string): boolean;
  /** True while the gate holds this key armed. */
  armed(key: string): boolean;
  /** The label of an armed control: the rest face and the armed face that
   *  names the act, in one cell, so the press keeps its width (R2069). */
  armLabel(key: string, rest: string, armFace: string): string;
}

/** One game screen. */
export interface GameView {
  /** The markup of the whole screen, as one string. */
  html(ctx: GameCtx): string;
  /**
   * One released button of this screen. `op` is the button's `data-op`,
   * `key` its arm key. True means the screen took the release.
   */
  act(op: string, btn: HTMLButtonElement, key: string, ctx: GameCtx): boolean;
  /**
   * One casino keybinding (group `casino`). True means the screen took
   * the press. A screen that has no use for the key returns false.
   */
  key(id: MapKeyId, ctx: GameCtx): boolean;
}

// --- the per-part contract (R988) ---------------------------------------
//
// `CasinoPanel.sync` writes the host only where the markup changed. A
// screen that answered with ONE string made every change a whole rewrite,
// and a rewrite throws the wheel and the reels away: the animation started
// again four times a second, so the ball never reached its pocket.
//
// **A screen therefore answers with a sequence of PARTS.** `part()` below
// wraps one top-level element in two marker comments and puts
// `data-part="<name>"` on it. `splitParts()` reads that sequence back: the
// GLUE between the parts, plus the part names in order, is the SHAPE, and
// the markup of each part stands under its own name.
//
// Five rules bind a screen:
//
// 1. One name is used one time in one screen. The panel finds a part by
//    `querySelector('[data-part=…]')`, and a repeat would have no anchor.
// 2. A part is never inside another part. The sequence is flat, so one
//    part can change while every other part stands.
// 3. Every countdown, every timer bar and every line that steps with the
//    clock goes in a SMALL part of its own. That part is rewritten many
//    times a hand, so nothing expensive may share it.
// 4. A part that holds an animation — `wheel`, `reels` — must answer with
//    markup that changes ONLY when the spin target or the hand changed.
//    The browser then runs the travel to its end.
// 5. The shape may change. The panel answers a new shape with one whole
//    rewrite, which is what a new phase, a new screen and a new table
//    want.

/** Opens one part. Nothing else in the client writes this text. The
 *  fence lives only in the markup of one render, so it took the short
 *  form `ap` with no step for an old name (R1676). */
const PART_OPEN = '<!--ap:';

/** Closes one part. */
const PART_CLOSE = '<!--/ap-->';

/**
 * One top-level part of a screen.
 *
 * `attrs` is everything else the root element carries — its class, an
 * inline geometric property — and `inner` is the markup under it. The
 * helper writes `data-part`, so a screen states the name one time.
 */
export function part(name: string, attrs: string, inner: string, tag = 'div'): string {
  return `${PART_OPEN}${name}-->`
    + `<${tag} data-part="${name}"${attrs === '' ? '' : ` ${attrs}`}>${inner}</${tag}>`
    + PART_CLOSE;
}

/** One screen's markup, read back as its shape and its parts. */
export interface ScreenParts {
  /**
   * The glue between the parts, plus the part names, in order. Two renders
   * that share a shape are patched part by part; every other pair takes
   * one whole rewrite.
   */
  shape: string;
  /** The markup of each part, by its name. */
  markup: Map<string, string>;
}

/**
 * Reads one screen's markup back as parts, or null when it holds none.
 *
 * Null is not a fault: the lobby and the shut card hold no animation, so
 * they take the whole rewrite they always took. Null also answers markup
 * this function cannot trust — an unclosed part, a repeated name — because
 * a wrong patch is worse than a rewrite.
 */
export function splitParts(html: string): ScreenParts | null {
  let open = html.indexOf(PART_OPEN);
  if (open < 0) return null;
  const shape: string[] = [];
  const markup = new Map<string, string>();
  let at = 0;
  while (open >= 0) {
    shape.push(html.slice(at, open));
    const nameEnd = html.indexOf('-->', open);
    if (nameEnd < 0) return null;
    const end = html.indexOf(PART_CLOSE, nameEnd);
    if (end < 0) return null;
    const name = html.slice(open + PART_OPEN.length, nameEnd);
    if (name === '' || markup.has(name)) return null;
    markup.set(name, html.slice(nameEnd + 3, end));
    // The name rides in the shape inside its own open mark, which no glue
    // carries because the split consumed every one, so two different name
    // lists can never read as one shape (R1332). The shape is then the
    // markup with the body of every part cut out.
    shape.push(PART_OPEN + name + '-->');
    at = end + PART_CLOSE.length;
    open = html.indexOf(PART_OPEN, at);
  }
  shape.push(html.slice(at));
  return { shape: shape.join(''), markup };
}
